/**
 * ProjectUsersTab - Manage users assigned to a project
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import projectsService, { ProjectUser, ProjectUserAssignment } from '../services/projects';
import { usersAPI, User } from '../services/users';
import { useAuth } from '../context/AuthContext';
import {
  isSuperAdmin,
  hasPermission,
  getAllowedUserTypes,
  getFilterableUserTypes,
} from '../utils/userPermissions';

interface ProjectUsersTabProps {
  projectId: string;
  /** Company the project belongs to — scopes the assignable-user list for super admins */
  companyId?: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const ProjectUsersTab: React.FC<ProjectUsersTabProps> = ({
  projectId,
  companyId,
  onError,
  onSuccess,
}) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ProjectUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Form fields for adding user
  const [selectedUserId, setSelectedUserId] = useState('');

  // Assignable users, loaded when the dialog first opens. Non-super-admins
  // are company-scoped server-side; super admins pass the project's company.
  const [availableUsers, setAvailableUsers] = useState<User[] | null>(null);
  useEffect(() => {
    if (openAddDialog && availableUsers === null) {
      usersAPI
        .getUsers({
          enabled: true,
          size: 100,
          ...(isSuperAdmin(currentUser) && companyId ? { company_id: companyId } : {}),
        })
        .then((res) => setAvailableUsers(res.users))
        .catch((err: any) => {
          setAvailableUsers([]);
          onError(err.response?.data?.detail || 'Failed to load users');
        });
    }
  }, [openAddDialog, availableUsers, companyId, currentUser, onError]);

  const userLabel = (u: User): string => {
    const name = u.full_name || [u.FirstName, u.LastName].filter(Boolean).join(' ') || u.UserName;
    return u.EmailAddress ? `${name} (${u.EmailAddress})` : name;
  };

  // ── Quick-create user (users.create: Super Admin or Client Admin) ──
  const canCreateUsers = hasPermission(currentUser, 'users.create');
  const EMPTY_NEW_USER = {
    FirstName: '',
    LastName: '',
    UserName: '',
    EmailAddress: '',
    Password: '',
    UserType: '04', // Project Admin — the usual role for people added per-project
  };
  const [createMode, setCreateMode] = useState(false);
  const [newUserForm, setNewUserForm] = useState(EMPTY_NEW_USER);
  const [creating, setCreating] = useState(false);

  const allowedTypes = getAllowedUserTypes(currentUser);
  const userTypeOptions = getFilterableUserTypes(currentUser).filter((t) =>
    allowedTypes.includes(t.value)
  );

  const closeAddDialog = () => {
    setOpenAddDialog(false);
    setCreateMode(false);
    setNewUserForm(EMPTY_NEW_USER);
    setSelectedUserId('');
  };

  const handleCreateAndAssign = async () => {
    if (!newUserForm.UserName.trim() || !newUserForm.Password) {
      onError('Username and password are required');
      return;
    }
    const targetCompany = companyId || String(currentUser?.CompanyID || currentUser?.companyid || '');
    setCreating(true);
    try {
      const created = await usersAPI.createUser({
        UserName: newUserForm.UserName.trim(),
        Password: newUserForm.Password,
        CompanyID: targetCompany,
        EmailAddress: newUserForm.EmailAddress.trim() || undefined,
        FirstName: newUserForm.FirstName.trim() || undefined,
        LastName: newUserForm.LastName.trim() || undefined,
        UserType: newUserForm.UserType,
      });
      await projectsService.assignUser(projectId, { user_id: created.UserID });
      onSuccess('User created and assigned to project');
      setAvailableUsers((prev) => (prev ? [...prev, created] : prev));
      closeAddDialog();
      loadUsers();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectsService.getProjectUsers(projectId);
      setUsers(data);
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [projectId, onError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async () => {
    if (!selectedUserId) {
      onError('Please select a user');
      return;
    }
    
    setLoading(true);
    try {
      const data: ProjectUserAssignment = {
        user_id: selectedUserId
        // No role needed - comes from user's UserType
      };
      
      await projectsService.assignUser(projectId, data);
      onSuccess('User assigned successfully');
      setOpenAddDialog(false);
      setSelectedUserId('');
      loadUsers();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to assign user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = (userId: string) => {
    setUserToDelete(userId);
    setDeleteConfirmOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      await projectsService.removeUser(projectId, userToDelete);
      onSuccess('User removed successfully');
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCancel = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Assigned Users</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddDialog(true)}
          disabled={loading}
        >
          Assign User
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    No users assigned yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>{user.user_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveClick(user.user_id)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createMode ? 'Create New User' : 'Assign User to Project'}
        </DialogTitle>
        <DialogContent>
          {createMode ? (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First Name"
                  value={newUserForm.FirstName}
                  onChange={(e) => setNewUserForm((p) => ({ ...p, FirstName: e.target.value }))}
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Last Name"
                  value={newUserForm.LastName}
                  onChange={(e) => setNewUserForm((p) => ({ ...p, LastName: e.target.value }))}
                  fullWidth
                />
              </Box>
              <TextField
                label="Username"
                value={newUserForm.UserName}
                onChange={(e) => setNewUserForm((p) => ({ ...p, UserName: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={newUserForm.EmailAddress}
                onChange={(e) => setNewUserForm((p) => ({ ...p, EmailAddress: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={newUserForm.Password}
                onChange={(e) => setNewUserForm((p) => ({ ...p, Password: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                select
                label="Role"
                value={newUserForm.UserType}
                onChange={(e) => setNewUserForm((p) => ({ ...p, UserType: e.target.value }))}
                fullWidth
              >
                {userTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                fullWidth
                loading={availableUsers === null}
                options={(availableUsers ?? []).filter(
                  (u) => !users.some((assigned) => assigned.user_id === u.UserID)
                )}
                getOptionLabel={userLabel}
                isOptionEqualToValue={(option, val) => option.UserID === val.UserID}
                value={(availableUsers ?? []).find((u) => u.UserID === selectedUserId) || null}
                onChange={(_, selected) => setSelectedUserId(selected?.UserID || '')}
                renderInput={(params) => (
                  <TextField {...params} label="User" placeholder="Search users..." />
                )}
                noOptionsText={
                  availableUsers === null ? 'Loading users...' : 'No users available to assign'
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {createMode ? (
            <>
              <Button onClick={() => setCreateMode(false)} disabled={creating}>
                Back
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={closeAddDialog} disabled={creating}>Cancel</Button>
              <Button
                onClick={handleCreateAndAssign}
                variant="contained"
                disabled={creating || !newUserForm.UserName.trim() || !newUserForm.Password}
              >
                {creating ? 'Creating...' : 'Create & Assign'}
              </Button>
            </>
          ) : (
            <>
              {canCreateUsers && (
                <Button
                  startIcon={<PersonAddIcon />}
                  onClick={() => setCreateMode(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Create New User
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={closeAddDialog}>Cancel</Button>
              <Button onClick={handleAddUser} variant="contained" disabled={loading || !selectedUserId}>
                Assign
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this user from the project?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel}>Cancel</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained" disabled={loading}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectUsersTab;

